<script setup>
import SecondaryTopBar from '@/components/SecondaryTopBar.vue';
import { onMounted } from 'vue';
import { useRoute } from 'vue-router';
import AppTopbar from './AppTopbar.vue';

const route = useRoute();


/**
 * Closes out the pre-paint loader in index.html.
 *
 * There is deliberately no Vue preloader component any more. One mounted here
 * could never show the loading it was meant to cover: by the time this layout
 * exists the router, the auth store and the bundle have already done their
 * work, so it appeared with its bar effectively finished and vanished at once.
 * The loader that can cover the wait is the one in the document, and all that is
 * left for Vue to do is end it honestly.
 *
 * Minimum display time is counted from navigation start, not from mount, so a
 * warm cache does not produce a flash of branding nobody can read.
 */
const PRELOADER_MIN_DISPLAY_MS = 1400;
const PRELOADER_COMPLETE_MS = 220;
const PRELOADER_FADE_MS = 420;

onMounted(() => {
  const preloader = document.getElementById('app-preloader');
  if (!preloader) return;

  /**
   * Measured from when the loader appeared, not from navigation start. The
   * render-blocking CDN scripts in index.html mean those two moments are
   * seconds apart, and using the wrong one dismissed the loader in the same
   * frame it became visible.
   */
  const shownAt = window.__preloaderShownAt ?? 0;
  const visibleFor = performance.now() - shownAt;
  const remaining = Math.max(0, PRELOADER_MIN_DISPLAY_MS - visibleFor);

  setTimeout(() => {
    // Fills the last 8% of the bar: the first moment we can honestly claim 100%
    preloader.classList.add('is-complete');

    setTimeout(() => {
      preloader.classList.add('is-leaving');
      setTimeout(() => preloader.remove(), PRELOADER_FADE_MS);
    }, PRELOADER_COMPLETE_MS);
  }, remaining);
});

</script>
<template>
  <div class="w-full overflow-hidden h-full flex flex-col">
    <SecondaryTopBar
      class="secondary-top-bar w-full"
      v-if="route.name != 'login'"
    ></SecondaryTopBar>
    <AppTopbar
      class="top-bar w-full drop-shadow z-50"
      v-if="route.name != 'login'"
    />
    <router-view
      class="w-full"
      :class="{
        'h-[89%]': route.name != 'login',
        'h-[95%]': route.name === 'map',
        'h-full': route.name === 'login',
      }"
    ></router-view>
    <!--
      La riga di copyright sta sopra tutto e non riceve clic: quello che c'e'
      sotto - la mappa, un grafico - continua a funzionare attraverso di essa.
    -->
    <footer
      class="fixed bottom-0 left-0 right-0 flex items-center justify-center z-[9999] pointer-events-none"
      style="height: 24px"
      v-if="route.name != 'login'"
    >
      <!--
        Un riquadro sfocato, non solo del testo.

        Prima era grigio al 30% di opacita' sopra qualunque cosa ci fosse: su
        una mappa satellitare o su un grafico spariva del tutto, e una riga di
        copyright che non si legge vale come non scritta. Sfocare cio' che sta
        dietro invece di coprirlo con un rettangolo pieno la rende leggibile
        senza nascondere nulla: si intuisce ancora cosa c'e' sotto.
      -->
      <span class="copyright-badge">Copyright Agricobots&reg;, 2026</span>
    </footer>

  </div>
</template>

<style>
.copyright-badge {
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  line-height: 1.2;
  letter-spacing: 0.02em;
  color: rgb(71 85 105 / 85%);
  background: rgb(255 255 255 / 55%);
  border: 1px solid rgb(148 163 184 / 30%);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

/*
  Il tema scuro e' quello predefinito di questa dashboard: sul fondo chiaro del
  riquadro il testo scuro sparirebbe, quindi si invertono entrambi.
*/
.app-dark .copyright-badge {
  color: rgb(203 213 225 / 85%);
  background: rgb(15 23 42 / 55%);
  border-color: rgb(148 163 184 / 25%);
}

.secondary-top-bar {
  height: 5%;
}
.top-bar {
  height: 6%;
}
</style>
